import type { RiskSeverity, RiskStatus, RoomItemVisibility, RoomRisk } from '../api/types';

// The risks & issues log and timeline milestones (ADR-0047): the words every app
// shows for the enums, and the one ordering the log uses. Entries are written by
// the deal team — the AI never adds, scores or judges them.

export const RISK_SEVERITIES: readonly RiskSeverity[] = ['high', 'medium', 'low'];
export const RISK_STATUSES: readonly RiskStatus[] = ['open', 'mitigating', 'resolved'];
export const ROOM_ITEM_VISIBILITIES: readonly RoomItemVisibility[] = ['deal_team', 'seller', 'all'];

export const RISK_SEVERITY_LABELS: Record<RiskSeverity, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  open: 'Open',
  mitigating: 'Mitigating',
  resolved: 'Resolved',
};

/** Who can read an entry — the visibility ladder in plain words. */
export const VISIBILITY_LABELS: Record<RoomItemVisibility, string> = {
  deal_team: 'Deal team only',
  seller: 'Deal team + seller',
  all: 'Everyone, including buyers',
};

/** The longest values the API accepts (mirrors the server's limits). */
export const RISK_TITLE_MAX = 200;
export const RISK_DESCRIPTION_MAX = 4000;
export const RISK_OWNER_LABEL_MAX = 200;
export const MILESTONE_NAME_MAX = 120;

const SEVERITY_RANK: Record<RiskSeverity, number> = { high: 0, medium: 1, low: 2 };
const STATUS_RANK: Record<RiskStatus, number> = { open: 0, mitigating: 1, resolved: 2 };

/**
 * The log's order: unresolved entries (open, then mitigating) before resolved
 * ones; within each, high severity first, then the nearest due date (no date
 * last), then newest. Pure — returns a new array.
 */
export function sortRisks(risks: readonly RoomRisk[]): RoomRisk[] {
  return [...risks].sort((a, b) => {
    const resolved = Number(a.status === 'resolved') - Number(b.status === 'resolved');
    if (resolved !== 0) return resolved;
    const severity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (severity !== 0) return severity;
    const status = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (status !== 0) return status;
    if (a.dueDate !== b.dueDate) {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate < b.dueDate ? -1 : 1;
    }
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/** The entries the log shows: resolved ones only when asked for. */
export function visibleRisks(risks: readonly RoomRisk[], showResolved: boolean): RoomRisk[] {
  return sortRisks(showResolved ? risks : risks.filter((r) => r.status !== 'resolved'));
}
